// One server-corrected clock for every countdown that has no status payload of
// its own to correct against.
//
// Countdowns must not run on `Date.now()` alone: however far the phone's clock
// is off, the countdown is off by the same amount. Two phones side by side
// showed the same round 20 seconds apart (2026-09-04). The room pages fix this
// per screen from their own status query (`clockSkewSec`); this store lets any
// screen that has seen a `serverTime` publish the skew, and any countdown read
// it — device skew is a property of the device, not of the endpoint that
// happened to measure it.
import { useEffect, useState } from 'react';
import { create } from 'zustand';

import { clockSkewSec } from './clockSkew.ts';

type ServerClockState = {
  /** Seconds to add to the device clock. 0 until a server time has been seen. */
  skewSec: number;
  /** When (device ms) the sample behind `skewSec` arrived; 0 = never. */
  sampledAtMs: number;
  /**
   * Feed a `serverTime` from any response, with react-query's `dataUpdatedAt`
   * as the arrival time. Ignored when either is missing.
   */
  reportServerTime: (serverEpochSec: number | null | undefined, receivedAtMs: number | null | undefined) => void;
};

export const useServerClock = create<ServerClockState>((set) => ({
  skewSec: 0,
  sampledAtMs: 0,
  reportServerTime: (serverEpochSec, receivedAtMs) => {
    const server = Number(serverEpochSec);
    const received = Number(receivedAtMs);
    if (!Number.isFinite(server) || server <= 0 || !Number.isFinite(received) || received <= 0) return;
    const skewSec = clockSkewSec(server, received);
    set((state) => (state.skewSec === skewSec && state.sampledAtMs === received ? state : { skewSec, sampledAtMs: received }));
  },
}));

/** Device clock plus the published skew, seconds. Safe outside React. */
export function serverNowSec(deviceMs: number = Date.now()): number {
  return Math.floor(deviceMs / 1000) + useServerClock.getState().skewSec;
}

/** Seconds since epoch on the server's clock, ticking once a second. */
export function useServerNowSeconds(): number {
  const skewSec = useServerClock((state) => state.skewSec);
  const [deviceSec, setDeviceSec] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = window.setInterval(() => setDeviceSec(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(id);
  }, []);
  return deviceSec + skewSec;
}
